import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Minus, X, Loader2, GripVertical, ChevronDown, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { termsToHtml } from "../lib/terms";
import { CURRENCIES, TAX_TREATMENTS, formatMoney, currencySymbol, defaultTreatment, taxRate, taxLabel } from "../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => Math.random().toString(36).slice(2, 9);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmt = (s) => s ? String(s).slice(0, 10).split("-").reverse().join("-") : "";
const contactLabel = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Contact";
const prettyStatus = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function QuoteBuilderPage({ editId }) {
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState([]);
  const [settings, setSettings] = useState({});
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState("EQ-…");

  const [chosen, setChosen] = useState(null); // {type,id,name}
  const [recSearch, setRecSearch] = useState("");
  const [recOpen, setRecOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [manualContact, setManualContact] = useState(false);
  const [validUntil, setValidUntil] = useState("");

  const [lines, setLines] = useState([]);
  const [lineSearch, setLineSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [termsId, setTermsId] = useState("none");
  const [cur, setCur] = useState("AUD");
  const [tax, setTax] = useState("domestic");
  const [termsOpenFull, setTermsOpenFull] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragUid = useRef(null);

  const validDays = settings.default_valid_days || 30;
  const money2 = (v) => formatMoney(v, cur);
  const GST = taxRate(tax);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cat, set, cl, pr, qs] = await Promise.all([
          axios.get(`${API}/quotes/catalogue`).catch(() => ({ data: {} })),
          axios.get(`${API}/quotes/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/customer-success/clients`).catch(() => ({ data: {} })),
          axios.get(`${API}/sales/prospects`, { params: { limit: 500 } }).catch(() => ({ data: {} })),
          axios.get(`${API}/quotes`).catch(() => ({ data: {} })),
        ]);
        setCatalogue((cat.data.catalogue || []).filter((c) => c.active !== false));
        const s = set.data.settings || {};
        setSettings(s);
        const qCount = {};
        (qs.data.quotes || []).forEach((q) => { if (q.link_id) qCount[q.link_id] = (qCount[q.link_id] || 0) + 1; });
        const recs = [
          ...(cl.data.clients || []).map((c) => ({ type: "client", id: c.client_id, name: c.company_name, currency: c.currency || "AUD", context: qCount[c.client_id] ? `${qCount[c.client_id]} quote${qCount[c.client_id] > 1 ? "s" : ""}` : "No quotes" })),
          ...(pr.data.prospects || []).map((p) => ({ type: "prospect", id: p.id, name: p.company_name, currency: p.currency || "AUD", context: qCount[p.id] ? `${qCount[p.id]} quote${qCount[p.id] > 1 ? "s" : ""}` : prettyStatus(p.status) })),
        ];
        setRecords(recs);

        if (editId) {
          const r = await axios.get(`${API}/quotes/${editId}`);
          const q = r.data.quote;
          setNumber(q.quote_number);
          setCur(q.currency || "AUD");
          setTax(q.tax_treatment || defaultTreatment(q.currency));
          setChosen({ type: q.link_type, id: q.link_id, name: q.link_name });
          setContactName(q.contact_name || ""); setContactEmail(q.contact_email || "");
          setValidUntil((q.valid_until || "").slice(0, 10) || addDays(validDays));
          setNotes(q.notes || "");
          setLines((q.line_items || []).map((l) => ({ uid: uid(), code: l.code || null, description: l.description, qty: l.qty || 1, unit_price: l.unit_price || 0, billing: l.billing === "monthly" ? "monthly" : "one_off", optional: !!l.optional })));
          const merged = [];
          if ((s.default_terms || "").trim()) merged.push({ id: "default", name: "Default", body: s.default_terms });
          merged.push(...(s.terms_templates || []));
          const tpl = merged.find((t) => t.body === q.terms);
          setTermsId(tpl ? tpl.id : (q.terms ? "custom" : "none"));
          loadContacts(q.link_type, q.link_id);
        } else {
          const nn = await axios.get(`${API}/quotes/next-number`).catch(() => ({ data: {} }));
          setNumber(nn.data.quote_number || "EQ-…");
          setValidUntil(addDays(s.default_valid_days || 30));
          if ((s.default_terms || "").trim()) setTermsId("default");
          else if ((s.terms_templates || []).length) setTermsId(s.terms_templates[0].id);
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const loadContacts = async (type, id) => {
    const url = type === "client" ? `${API}/customer-success/clients/${encodeURIComponent(id)}/contacts` : `${API}/sales/prospects/${encodeURIComponent(id)}/contacts`;
    try { const r = await axios.get(url); setContacts(r.data.contacts || []); } catch (e) { setContacts([]); }
  };

  const chooseRecord = (r) => {
    setChosen(r); setRecOpen(false); setRecSearch(""); setContacts([]); setContactName(""); setContactEmail(""); setManualContact(false);
    // The record's own currency decides how they get quoted (and therefore the tax).
    if (!editId) { const c = (r.currency || "AUD").toUpperCase(); setCur(c); setTax(defaultTreatment(c)); }
    loadContacts(r.type, r.id);
  };

  const pickCurrency = (c) => { setCur(c); setTax(defaultTreatment(c)); };

  const recResults = useMemo(() => {
    const q = recSearch.trim().toLowerCase();
    if (!q) return records.slice(0, 40);
    return records.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 40);
  }, [records, recSearch]);

  const catResults = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (q.length < 1) return [];
    return catalogue.filter((c) => (c.code || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [catalogue, lineSearch]);

  const priceFor = (c) => {
    const p = (c.prices || {})[cur];
    return p === undefined || p === null ? null : Number(p);
  };

  const addCatLine = (c) => {
    const p = priceFor(c);
    if (p === null) toast.warning(`${c.code} has no ${cur} price yet — set one in the Product Library.`);
    setLines((ls) => [...ls, { uid: uid(), code: c.code, description: c.name, qty: 1, unit_price: p ?? 0, billing: c.default_billing === "monthly" ? "monthly" : "one_off", optional: false }]);
    setLineSearch("");
  };
  const addFreeLine = () => {
    setLines((ls) => [...ls, { uid: uid(), code: null, description: lineSearch.trim(), qty: 1, unit_price: 0, billing: "one_off", optional: false }]);
    setLineSearch("");
  };
  const patch = (u, k, v) => setLines((ls) => ls.map((l) => l.uid === u ? { ...l, [k]: v } : l));
  const remove = (u) => setLines((ls) => ls.filter((l) => l.uid !== u));

  const onDrop = (targetUid, group) => {
    const from = dragUid.current; dragUid.current = null;
    if (!from || from === targetUid) return;
    setLines((ls) => {
      const groupUids = ls.filter((l) => (l.billing === "monthly" ? "monthly" : "one_off") === group).map((l) => l.uid);
      if (!groupUids.includes(from) || !groupUids.includes(targetUid)) return ls;
      const arr = [...ls];
      const fi = arr.findIndex((l) => l.uid === from);
      const [moved] = arr.splice(fi, 1);
      const ti = arr.findIndex((l) => l.uid === targetUid);
      arr.splice(ti, 0, moved);
      return arr;
    });
  };

  const rec = lines.filter((l) => l.billing === "monthly");
  const off = lines.filter((l) => l.billing !== "monthly");
  const sub = (arr, opt) => arr.filter((l) => !!l.optional === opt).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const recSub = sub(rec, false), recOpt = sub(rec, true);
  const offSub = sub(off, false), offOpt = sub(off, true);
  const optionalCount = lines.filter((l) => l.optional).length;

  const ready = !!chosen && lines.length > 0;
  const gateReason = !chosen ? "Choose a client or prospect to continue."
    : lines.length === 0 ? "Add at least one line item to continue."
    : `Creates quote ${number} as a draft. Nothing is emailed until you send it.`;

  const defaultTermsEntry = (settings.default_terms || "").trim() ? [{ id: "default", name: "Default", body: settings.default_terms }] : [];
  const termsList = [...defaultTermsEntry, ...(settings.terms_templates || [])];
  const selectedTerms = termsList.find((t) => t.id === termsId);

  const buildBody = () => ({
    link_name: chosen.name,
    contact_name: contactName || null,
    contact_email: contactEmail || null,
    valid_until: validUntil || null,
    currency: cur,
    tax_treatment: tax,
    notes,
    terms: termsId === "none" ? "" : (selectedTerms ? selectedTerms.body : ""),
    line_items: lines.map((l) => ({ code: l.code, description: l.description, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0, billing: l.billing, optional: !!l.optional })),
  });

  const save = async () => {
    if (!chosen) { toast.error("Choose a record first"); return; }
    if (lines.some((l) => !l.description.trim())) { toast.error("Every line needs a description"); return; }
    setSaving(true);
    try {
      if (editId) { await axios.put(`${API}/quotes/${editId}`, buildBody()); toast.success("Draft saved"); }
      else { await axios.post(`${API}/quotes`, { ...buildBody(), link_type: chosen.type, link_id: chosen.id }); toast.success(`Quote ${number} created as a draft`); }
      navigate(-1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#8A857A] p-6" data-testid="qb-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading builder…</div>;

  const renderLineRow = (l, group) => (
    <div key={l.uid} draggable onDragStart={() => (dragUid.current = l.uid)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(l.uid, group)}
      className="grid items-center bg-white" style={{ gridTemplateColumns: "22px minmax(0,1fr) 92px 104px 84px 30px", columnGap: 10, padding: "12px 16px", borderTop: "1px solid #F0EDE6" }} data-testid={`qb-line-${l.uid}`}>
      <span className="cursor-grab text-[#C9C3B6]"><GripVertical className="w-3.5 h-3.5" /></span>
      <div className="min-w-0">
        <input value={l.description} onChange={(e) => patch(l.uid, "description", e.target.value)} className="w-full h-9 px-2.5 rounded-md text-[13px] text-[#1A1A1A] outline-none" style={{ border: "1px solid #DAD5CA" }} data-testid={`qb-desc-${l.uid}`} />
        <div className="flex items-center gap-2 mt-1">
          {l.code && <span className="mono text-[10px] font-bold text-[#8A857A]">{l.code}</span>}
          <button onClick={() => patch(l.uid, "optional", !l.optional)} data-testid={`qb-optional-${l.uid}`}
            className="text-[10.5px] font-semibold rounded px-1.5 py-0.5 transition-colors"
            style={l.optional ? { background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8" } : { background: "transparent", border: "1px solid #DAD5CA", color: "#59554D" }}>
            {l.optional ? "Optional line" : "Make optional"}
          </button>
        </div>
      </div>
      <div className="flex items-center rounded-md overflow-hidden" style={{ border: "1px solid #DAD5CA", height: 36 }}>
        <button disabled={l.qty <= 1} onClick={() => patch(l.uid, "qty", Math.max(1, (Number(l.qty) || 1) - 1))} className="w-8 h-full flex items-center justify-center disabled:text-[#C9C3B6] text-[#59554D]" style={{ borderRight: "1px solid #E6E2D9", cursor: l.qty <= 1 ? "default" : "pointer" }}><Minus className="w-3 h-3" /></button>
        <span className="flex-1 text-center mono text-[12.5px] font-bold text-[#1A1A1A]">{l.qty}</span>
        <button onClick={() => patch(l.uid, "qty", (Number(l.qty) || 1) + 1)} className="w-8 h-full flex items-center justify-center text-[#59554D]" style={{ borderLeft: "1px solid #E6E2D9" }}><Plus className="w-3 h-3" /></button>
      </div>
      <div className="flex items-center rounded-md" style={{ border: "1px solid #DAD5CA", height: 36 }}>
        <span className="mono text-[12.5px] text-[#8A857A] pl-2.5">{currencySymbol(cur)}</span>
        <input value={l.unit_price} onChange={(e) => patch(l.uid, "unit_price", e.target.value.replace(/[^0-9.]/g, ""))} className="w-full h-full px-2 text-right mono text-[12.5px] text-[#1A1A1A] outline-none bg-transparent" data-testid={`qb-price-${l.uid}`} />
      </div>
      <div className="mono text-[12.5px] font-bold text-right text-[#1A1A1A]">{money2((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</div>
      <button onClick={() => remove(l.uid)} className="w-[30px] h-[30px] rounded-md flex items-center justify-center text-[#8A857A] hover:bg-[#FFEDE4] hover:text-[#8F2E00]" data-testid={`qb-remove-${l.uid}`}><X className="w-4 h-4" /></button>
    </div>
  );

  const renderBlock = ({ title, sub2, group, items, empty }) => (
    <div key={group}>
      <div className="flex items-center justify-between px-4 py-2" style={{ background: "#FBFAF7", borderTop: "1px solid #E6E2D9" }}>
        <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">{title}</span>
        <span className="mono text-[11.5px] text-[#59554D]">{money2(sub2)} {group === "monthly" ? "/mo " : ""}{tax === "export" ? `${cur}` : "ex GST"}</span>
      </div>
      {items.length === 0 ? <div className="px-4 py-3 text-[12px] text-[#8A857A]">{empty}</div> : items.map((l) => renderLineRow(l, group))}
    </div>
  );

  const renderTotals = ({ sub2, opt, recurring }) => (
    <div className="px-4 py-2" style={{ background: "#fff" }}>
      <div className="flex justify-between text-[12.5px] text-[#59554D] py-0.5"><span>Subtotal</span><span className="mono">{money2(sub2)}{recurring ? " /mo" : ""}</span></div>
      <div className="flex justify-between text-[12.5px] text-[#59554D] py-0.5"><span>{taxLabel(tax)}</span><span className="mono">{money2(sub2 * GST)}{recurring ? " /mo" : ""}</span></div>
      <div className="flex justify-between text-[13px] font-bold text-[#1A1A1A] py-0.5"><span>Total</span><span className="mono">{money2(sub2 * (1 + GST))}{recurring ? " /mo" : ""}</span></div>
      {opt > 0 && <div className="flex justify-between text-[12.5px] font-semibold py-0.5" style={{ color: "#1D4ED8" }}><span>If optional lines accepted</span><span className="mono">{money2((sub2 + opt) * (1 + GST))}{recurring ? " /mo" : ""}</span></div>}
    </div>
  );

  const renderPreviewLines = (items, recurring) => items.map((l) => (
    <div key={l.uid} className="grid px-4 py-1.5" style={{ gridTemplateColumns: "minmax(0,1fr) 34px 84px", columnGap: 8, borderTop: "1px solid #F5F2EC" }}>
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-[#1A1A1A] truncate">{l.description || <span className="text-[#B5AFA3]">Untitled line</span>}</div>
        {l.optional && <span className="text-[9.5px] font-bold tracking-[0.04em] px-1.5 py-0.5 rounded inline-block mt-0.5" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8" }}>OPTIONAL — ACCEPT OR DECLINE</span>}
      </div>
      <div className="mono text-[11.5px] text-right text-[#8A857A]">×{l.qty}</div>
      <div className="mono text-[12px] text-right text-[#1A1A1A]">{money2((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}{recurring ? " /mo" : ""}</div>
    </div>
  ));

  return (
    <div className="space-y-3.5" data-testid="quote-builder-page">
      {/* Breadcrumb */}
      <div className="text-[11.5px] flex items-center gap-1.5">
        <button onClick={() => navigate("/")} className="text-[#6B5600] hover:underline">Sales</button>
        <span className="text-[#B5AFA3]">/</span>
        <button onClick={() => navigate(-1)} className="text-[#6B5600] hover:underline">Quotes</button>
        <span className="text-[#B5AFA3]">/</span>
        <span className="text-[#1A1A1A] font-semibold">{editId ? number : "New quote"}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[25px] font-extrabold text-[#1A1A1A] tracking-[-0.018em]" style={{ fontFamily: "Manrope, sans-serif" }}>{editId ? "Edit quote" : "New quote"}</h1>
            <span className="inline-flex items-center gap-1.5"><span className="rounded-full" style={{ width: 5, height: 5, background: "#8A857A" }} /><span className="text-[12.5px] text-[#59554D]">Draft</span></span>
            <span className="mono text-[11.5px] text-[#8A857A]">{number}</span>
          </div>
          <p className="text-[13px] text-[#59554D] mt-0.5">{tax === "export" ? `Prices are entered in ${cur}. No tax is applied — this is a tax-free export.` : "Unit prices are entered excluding GST. The preview shows what the customer receives."}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="text-[13px] font-semibold text-[#6B5600] hover:underline px-2" data-testid="qb-discard">Discard</button>
            <Button variant="outline" onClick={save} disabled={saving || !chosen} className="h-[34px] bg-white border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#F4F2ED] text-[13px] font-semibold" data-testid="qb-save-draft">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Save draft</Button>
            <Button onClick={save} disabled={!ready || saving} title={gateReason} data-testid="qb-create"
              className="h-[34px] text-[12px] uppercase tracking-[0.04em] font-bold disabled:cursor-default"
              style={ready ? { background: "#FFD108", border: "1px solid #E0B800", color: "#1A1A1A" } : { background: "#E6E2D9", border: "1px solid #D3CEC2", color: "#9A958B" }}>
              <Check className="w-4 h-4 mr-1.5" /> Create quote
            </Button>
          </div>
          <p className="text-[11.5px] text-[#59554D] text-right max-w-[360px]">{gateReason}</p>
        </div>
      </div>

      {/* Two columns */}
      <div className="grid gap-3.5 items-start" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))" }}>
        {/* FORM */}
        <div className="space-y-3.5 min-w-0">
          {/* Who it is for */}
          <div className="bg-white rounded-xl border border-[#DAD5CA] overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
            <div className="px-4 py-3 border-b border-[#E6E2D9]"><span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Who it is for</span></div>
            <div className="p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">Client or prospect</div>
              {chosen && !recOpen ? (
                <div className="flex items-center gap-2 rounded-lg px-3" style={{ background: "#FBFAF7", border: "1px solid #E6E2D9", height: 40 }}>
                  <span className="text-[13.5px] font-semibold text-[#1A1A1A] truncate">{chosen.name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded shrink-0" style={chosen.type === "prospect" ? { background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" } : { background: "#F1EEE6", border: "1px solid #DAD5CA", color: "#59554D" }}>{chosen.type}</span>
                  {!editId && <button onClick={() => setRecOpen(true)} className="ml-auto text-[12px] font-semibold text-[#6B5600] hover:underline" data-testid="qb-change-record">Change</button>}
                </div>
              ) : (
                <div>
                  <div className="relative">
                    <Search className="w-4 h-4 text-[#8A857A] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input autoFocus value={recSearch} onChange={(e) => setRecSearch(e.target.value)} placeholder="Type a company name — clients and prospects together" data-testid="qb-record-input"
                      className="w-full h-[42px] pl-9 pr-14 rounded-[9px] text-[13px] outline-none" style={{ border: "1.5px solid #1A1A1A" }} />
                    <span className="mono text-[10.5px] text-[#8A857A] absolute right-3 top-1/2 -translate-y-1/2">{recResults.length}</span>
                  </div>
                  <div className="mt-2 rounded-lg border border-[#E6E2D9] overflow-y-auto" style={{ maxHeight: 232 }}>
                    {recResults.map((r) => (
                      <button key={`${r.type}-${r.id}`} onClick={() => chooseRecord(r)} data-testid={`qb-record-${r.id}`} className="w-full flex items-center gap-2 px-3 py-2 border-b border-[#F0EDE6] hover:bg-[#FFFCEF] text-left">
                        <span className="text-[13px] font-semibold text-[#1A1A1A] truncate flex-1">{r.name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded" style={r.type === "prospect" ? { background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" } : { background: "#F1EEE6", border: "1px solid #DAD5CA", color: "#59554D" }}>{r.type}</span>
                        <span className="text-[11.5px] text-[#8A857A] w-[92px] text-right shrink-0">{r.context}</span>
                      </button>
                    ))}
                    {recResults.length === 0 && <div className="px-3 py-4 text-[12.5px] text-[#8A857A]">No matches.</div>}
                  </div>
                </div>
              )}

              {chosen && !recOpen && (
                <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Contact</div>
                    {!manualContact ? (
                      <select data-testid="qb-contact-select" className="w-full h-10 px-2 rounded-lg text-[13px] bg-white" style={{ border: "1px solid #DAD5CA" }}
                        value={contactEmail || "__none"}
                        onChange={(e) => {
                          if (e.target.value === "__manual") { setManualContact(true); setContactName(""); setContactEmail(""); return; }
                          const c = contacts.find((x) => (x.email || "") === e.target.value);
                          if (c) { setContactName(contactLabel(c)); setContactEmail(c.email || ""); }
                        }}>
                        <option value="__none" disabled>Select a contact…</option>
                        {contacts.map((c, i) => <option key={i} value={c.email || `c${i}`}>{contactLabel(c)}{c.email ? ` · ${c.email}` : ""}</option>)}
                        <option value="__manual">Add a new contact…</option>
                      </select>
                    ) : (
                      <div className="space-y-2">
                        <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Contact name" className="w-full h-9 px-2.5 rounded-md text-[13px] outline-none" style={{ border: "1px solid #DAD5CA" }} data-testid="qb-contact-name" />
                        <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Contact email" className="w-full h-9 px-2.5 rounded-md text-[13px] outline-none" style={{ border: "1px solid #DAD5CA" }} data-testid="qb-contact-email" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Valid until</div>
                    <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="w-full h-10 px-2.5 rounded-lg mono text-[13px] bg-white" style={{ border: "1px solid #DAD5CA" }} data-testid="qb-valid-until" />
                    <p className="text-[11px] text-[#8A857A] mt-1">{validDays} days from today. The customer sees this date.</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Currency</div>
                    <select data-testid="qb-currency" value={cur} onChange={(e) => pickCurrency(e.target.value)}
                      className="w-full h-10 px-2 rounded-lg text-[13px] bg-white" style={{ border: "1px solid #DAD5CA" }}>
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <p className="text-[11px] text-[#8A857A] mt-1">Prices come from this product's {cur} price list.</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Tax</div>
                    <select data-testid="qb-tax" value={tax} onChange={(e) => setTax(e.target.value)}
                      className="w-full h-10 px-2 rounded-lg text-[13px] bg-white" style={{ border: "1px solid #DAD5CA" }}>
                      {TAX_TREATMENTS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <p className="text-[11px] text-[#8A857A] mt-1">
                      {tax === "export" ? "Tax-free export — no Australian GST is collected." : "Australian supply — 10% GST is added."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <div className="bg-white rounded-xl border border-[#DAD5CA] overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E6E2D9]">
              <span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Line items</span>
              <span className="mono text-[11.5px] text-[#59554D]">{lines.length} line{lines.length !== 1 ? "s" : ""}{optionalCount ? ` · ${optionalCount} optional` : ""}</span>
              <span className="ml-auto text-[11px] text-[#8A857A]">Drag to reorder within a block.</span>
            </div>
            <div className="p-4 pb-0">
              <div className="relative">
                <Search className="w-4 h-4 text-[#8A857A] absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={lineSearch} onChange={(e) => setLineSearch(e.target.value)} placeholder="Type a product code or name, or write your own line" data-testid="qb-line-search"
                  className="w-full h-[42px] pl-9 pr-3 rounded-[9px] text-[13px] outline-none" style={{ border: "1.5px solid #1A1A1A" }} />
              </div>
              {lineSearch.trim().length >= 1 && (
                <div className="mt-2 rounded-lg border border-[#E6E2D9] overflow-hidden">
                  {catResults.map((c) => (
                    <button key={c.code} onClick={() => addCatLine(c)} data-testid={`qb-cat-${c.code}`} className="w-full grid items-center px-3 py-2 border-b border-[#F0EDE6] hover:bg-[#FFFCEF] text-left" style={{ gridTemplateColumns: "54px 1fr auto", columnGap: 10 }}>
                      <span className="mono text-[11px] font-bold text-[#8A857A]">{c.code}</span>
                      <span className="text-[13px] font-semibold text-[#1A1A1A] truncate">{c.name}</span>
                      <span className="mono text-[12px] text-[#1A1A1A]">{priceFor(c) === null ? <span className="text-[#C23A00]">No {cur} price</span> : money2(priceFor(c))}<span className="text-[10px] uppercase text-[#8A857A] ml-1">{c.default_billing === "monthly" ? "/MO" : "ONE-OFF"}</span></span>
                    </button>
                  ))}
                  <button onClick={addFreeLine} data-testid="qb-add-free" className="w-full px-3 py-2 text-left text-[13px] font-semibold text-[#1A1A1A]" style={{ background: "#FBFAF7" }}>+ Add "{lineSearch.trim()}" as a free-text line</button>
                </div>
              )}
            </div>
            <div className="mt-3">
              {renderBlock({ title: "Recurring · per month", group: "monthly", items: rec, sub2: recSub, empty: "No recurring lines. Add a subscription product above." })}
              {renderBlock({ title: "One-off", group: "one_off", items: off, sub2: offSub, empty: "No one-off lines. Implementation and migration live here." })}
            </div>
          </div>

          {/* Notes and terms */}
          <div className="bg-white rounded-xl border border-[#DAD5CA] overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
            <div className="px-4 py-3 border-b border-[#E6E2D9]"><span className="text-[14px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Notes and terms</span></div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Notes</div>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the customer should read alongside the pricing" className="min-h-[80px] text-[13.5px]" data-testid="qb-notes" />
                <p className="text-[11px] text-[#8A857A] mt-1">Shown on the quote, above the terms.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">Terms and conditions</div>
                  <select value={termsId} onChange={(e) => setTermsId(e.target.value)} className="h-8 px-2 rounded-md text-[12px] bg-white" style={{ border: "1px solid #DAD5CA" }} data-testid="qb-terms-select">
                    {termsList.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    {termsId === "custom" && <option value="custom">Existing terms</option>}
                    <option value="none">No terms</option>
                  </select>
                </div>
                {termsId !== "none" && (
                  <div className="rounded-lg px-3 py-2.5" style={{ background: "#FBFAF7", border: "1px solid #E6E2D9" }}>
                    <div className="text-[12.5px] font-bold text-[#1A1A1A]">{selectedTerms ? selectedTerms.name : "Existing terms"}</div>
                    <button onClick={() => setTermsOpenFull((v) => !v)} className="text-[12px] font-semibold text-[#6B5600] hover:underline mt-1 inline-flex items-center gap-1" data-testid="qb-terms-full"><ChevronDown className={`w-3.5 h-3.5 transition-transform ${termsOpenFull ? "rotate-180" : ""}`} /> Read the full terms</button>
                    {termsOpenFull && <div className="text-[13px] leading-[1.6] text-[#59554D] mt-2" dangerouslySetInnerHTML={{ __html: termsToHtml(selectedTerms ? selectedTerms.body : "") }} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* PREVIEW */}
        <div className="min-w-0" style={{ position: "sticky", top: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">Preview</span>
            <span className="text-[11.5px] text-[#8A857A]">What the customer receives</span>
          </div>
          <div className="bg-white rounded-xl border border-[#DAD5CA] overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }} data-testid="qb-preview">
            <div className="flex items-start justify-between px-4 py-4 border-b border-[#E6E2D9]">
              <div><div className="text-[15px] font-extrabold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>EQUIPR</div><div className="text-[12px] text-[#59554D]">Quotation</div></div>
              <div className="text-right"><div className="mono text-[12.5px] font-bold text-[#1A1A1A]">{number}</div><div className="text-[11px] text-[#8A857A]">Issued {fmt(new Date().toISOString())}</div><div className="text-[11px] text-[#8A857A]">Valid until {fmt(validUntil)}</div></div>
            </div>
            <div className="px-4 py-3 border-b border-[#E6E2D9]">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Prepared for</div>
              <div className="text-[13.5px] font-semibold text-[#1A1A1A]">{chosen ? chosen.name : <span className="text-[#B5AFA3]">No record chosen</span>}</div>
              {(contactName || contactEmail) && <div className="text-[12.5px] text-[#59554D]">{contactName}{contactEmail ? ` · ${contactEmail}` : ""}</div>}
            </div>

            {rec.length > 0 && (<>
              <div className="px-4 py-2" style={{ background: "#FBFAF7", borderBottom: "1px solid #E6E2D9" }}><span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">Recurring · per month</span></div>
              {renderPreviewLines(rec, true)}
              <div style={{ borderTop: "1px solid #E6E2D9" }}>{renderTotals({ sub2: recSub, opt: recOpt, recurring: true })}</div>
            </>)}
            {off.length > 0 && (<>
              <div className="px-4 py-2" style={{ background: "#FBFAF7", borderTop: "1px solid #E6E2D9", borderBottom: "1px solid #E6E2D9" }}><span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold">One-off</span></div>
              {renderPreviewLines(off, false)}
              <div style={{ borderTop: "1px solid #E6E2D9" }}>{renderTotals({ sub2: offSub, opt: offOpt, recurring: false })}</div>
            </>)}
            {lines.length === 0 && <div className="px-4 py-6 text-[12.5px] text-[#8A857A]">Add a line to see the customer's quote build here.</div>}
            {lines.length > 0 && (
              <div className="px-4 py-2.5 text-[11.5px] text-[#59554D]" style={{ borderTop: "1px solid #E6E2D9", background: "#FBFAF7" }} data-testid="qb-currency-note">
                All amounts in {CURRENCIES.find((c) => c.code === cur)?.name || cur}.{" "}
                {tax === "export" ? "No tax applicable — tax-free export." : "Totals include GST at 10%."}
              </div>
            )}

            {optionalCount > 0 && (
              <div className="mx-4 my-3 rounded-lg px-3 py-2 flex items-start gap-2" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                <span className="rounded-full mt-1.5 shrink-0" style={{ width: 5, height: 5, background: "#1D4ED8" }} />
                <p className="text-[12px]" style={{ color: "#1D4ED8" }}>{optionalCount} line{optionalCount > 1 ? "s are" : " is"} optional. The customer accepts or declines it when they respond, and the totals above show both outcomes.</p>
              </div>
            )}
            {termsId !== "none" && (
              <div className="px-4 py-3" style={{ borderTop: "1px solid #E6E2D9" }}>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Terms</div>
                <div className="text-[12.5px] text-[#1A1A1A]">{selectedTerms ? selectedTerms.name : "Terms attached"}</div>
                <div className="text-[11.5px] text-[#8A857A]">Attached in full to the emailed quote.</div>
              </div>
            )}
            {notes && (
              <div className="px-4 py-3" style={{ borderTop: "1px solid #E6E2D9" }}>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-bold mb-1">Notes</div>
                <p className="text-[12.5px] text-[#59554D] whitespace-pre-wrap">{notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
